import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { isEmpty, isNil, omit, pick } from "lodash-es";
import { routesArraySelector, useAppStore } from "../../../store";
import Access from "../Access";
import { ConfigProvider } from "antd";
import KeepAlive from "../KeepAlive";
import NotFoundPage from "../../../components/PageNotFound";
import { matchRoutes } from "react-router-dom";
// import { consoleInMicroApp } from "@/utils/devLog";
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var PanesContainer = function PanesContainer(props) {
  var _props$layoutConfig$p, _renderPanes$props$ac;
  var cachePanes = useRef({
    props: {},
    renders: {}
  });
  var pathname = useAppStore(function (s) {
    return s.pathname;
  });
  var layoutBasePath = useAppStore(function (s) {
    return s.layoutBasePath;
  });
  var _useTransition = useTransition(),
    _useTransition2 = _slicedToArray(_useTransition, 2),
    startTransition = _useTransition2[1];
  var _useState = useState(pathname),
    _useState2 = _slicedToArray(_useState, 2),
    activePathname = _useState2[0],
    setActivePathname = _useState2[1];
  var flatPathRoutes = useAppStore(function (s) {
    return s.flatPathRoutes;
  });
  var routesPaths = useAppStore(routesArraySelector);
  var _ref = matchRoutes(routesPaths, pathname) || [],
    _ref2 = _slicedToArray(_ref, 1),
    matchRoute = _ref2[0];
  var errorCaptureException = useAppStore(function (s) {
    return s.errorCaptureException;
  });
  var captureException = useAppStore(function (s) {
    var _s$monitorConfig;
    return (_s$monitorConfig = s.monitorConfig) === null || _s$monitorConfig === void 0 ? void 0 : _s$monitorConfig.captureException;
  });
  var appData = props.appData;

  // 面板信息
  var panes = useAppStore(function (s) {
    return s.panes;
  });
  var errorFallback = ((_props$layoutConfig$p = props.layoutConfig.paneErrorBoundary) === null || _props$layoutConfig$p === void 0 ? void 0 : _props$layoutConfig$p.fallback) || void 0;
  var onError = errorCaptureException || props.layoutConfig.errorCaptureException || captureException;
  useEffect(function () {
    startTransition(function () {
      return setActivePathname(pathname || "@@NOT_INIT");
    });
  }, [pathname]);
  var transitionCompleted = pathname === activePathname;

  // 缓存panes
  var renderPanes = useMemo(function () {
    var rendersInfo = panes.reduce(function (acc, cur) {
      var _cachePanes$current$p, _cachePanes$current$p2;
      // 路由索引path eg. /user/:id
      var path = cur.dynamicPath || cur.path;
      // 真实path eg. /user/123
      var userPath = cur.path;
      var id = Object.keys(appData.routes).find(function (key) {
        return appData.routes[key].path === path;
      });

      // 子路由应与父路由同路径，将path和id覆盖
      var subRouteId = Object.keys(appData.routes).find(function (key) {
        var parentId = appData.routes[key].parentId;
        var prevId = +key - 1;
        if (id && parentId === id && id === prevId.toString()) {
          return true;
        }
        return false;
      });
      id = subRouteId || id;
      var routeInfo = isNil(id) ? {} : appData.routes[id];
      var routeComponent = isNil(id) ? null : appData.routeComponents[id];
      var paneInfo = _objectSpread(_objectSpread(_objectSpread({
        routeComponent: routeComponent
      }, routeInfo), cur), {}, {
        id: id
      });
      // 缓存props以便对比面板是否更新
      var cacheProps = _objectSpread({}, pick(cur, ["path", "name", "search"]));
      cacheProps.search = cacheProps.search || "";
      acc.props[userPath] = cacheProps;
      // 保存特征值
      var feature = JSON.stringify(acc.props[userPath]);
      acc.props[userPath].feature = feature;
      var prevPaneCache = cachePanes.current.renders[userPath];
      acc.props[userPath].needRefresh = prevPaneCache && feature !== ((_cachePanes$current$p = cachePanes.current.props[userPath]) === null || _cachePanes$current$p === void 0 ? void 0 : _cachePanes$current$p.feature);
      if (
      // 上一次的缓存目标里存有相同内容,且特征值相同，则直接引用，不缓存
      prevPaneCache && feature === ((_cachePanes$current$p2 = cachePanes.current.props[userPath]) === null || _cachePanes$current$p2 === void 0 ? void 0 : _cachePanes$current$p2.feature)) {
        acc.renders[userPath] = prevPaneCache;
      } else {
        var LazyComponent = paneInfo.routeComponent;
        // 页面没有展示到前台，置空 || 子应用，避免渲染
        if (paneInfo.path !== activePathname || paneInfo.microApp) {
          acc.renders[userPath] = null;
          return acc;
        }
        acc.renders[userPath] = LazyComponent ? /*#__PURE__*/_jsx(Access, {
          layoutConfig: props.layoutConfig,
          route: paneInfo,
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            onError: onError,
            fallback: errorFallback,
            children: /*#__PURE__*/_createElement(LazyComponent, _objectSpread(_objectSpread({}, omit(paneInfo, "routeComponent", "key")), {}, {
              key: paneInfo.key
            }))
          })
        }) : null;
      }
      return acc;
    }, {
      props: {},
      renders: {}
    });
    cachePanes.current = rendersInfo;
    return rendersInfo;
  }, [activePathname, panes]);
  var NotFoundPagePlacer = props.layoutConfig.pageNotFoundFallback ? /*#__PURE__*/_jsx(_Fragment, {
    children: props.layoutConfig.pageNotFoundFallback(props.layoutConfig)
  }) : /*#__PURE__*/_jsx(NotFoundPage, {});
  var activeMenu = matchRoute ? flatPathRoutes[matchRoute.route.path] : null;
  var rendersInit = !isEmpty(renderPanes.renders);
  var routesInit = !isEmpty(flatPathRoutes);
  if (!routesInit || !rendersInit || activePathname === "@@NOT_INIT") {
    return null;
  }
  var activeFlag = activePathname.replace(/\//g, "");

  // 当前路由没有匹配到，展示404，如果匹配到的是框架基准path，则不展示内容，等待redirect
  var BaseDefaultPlacer = activePathname === layoutBasePath ? /*#__PURE__*/_jsx(_Fragment, {}) : NotFoundPagePlacer;
  var activeFlagClsName = "__sa-page-target__".concat(activeFlag);
  var activePaneDom = document.querySelector(".".concat(activeFlagClsName));
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      style: {
        display: transitionCompleted ? "none" : "block"
      }
    }), /*#__PURE__*/_jsx("div", {
      style: {
        display: transitionCompleted ? "block" : "none"
      },
      children: /*#__PURE__*/_jsx(KeepAlive, {
        activeName: activePathname,
        needRefresh: (_renderPanes$props$ac = renderPanes.props[activePathname]) === null || _renderPanes$props$ac === void 0 ? void 0 : _renderPanes$props$ac.needRefresh,
        cache: (activeMenu === null || activeMenu === void 0 ? void 0 : activeMenu.keepAlive) !== false,
        children: /*#__PURE__*/_jsx("div", {
          className: activeFlagClsName,
          children: /*#__PURE__*/_jsx(ConfigProvider, {
            getPopupContainer: function getPopupContainer() {
              return activePaneDom;
            },
            getTargetContainer: function getTargetContainer() {
              return activePaneDom;
            },
            children: activeMenu ? renderPanes.renders[activePathname] : BaseDefaultPlacer
          })
        })
      })
    })]
  });
};
export default PanesContainer;